import { useState } from "react";
import styles from "./avatar-system.module.css";
import AvatarUpload from "./AvatarUpload";
import AvatarGenerator from "./AvatarGenerator";
import AvatarDisplay from "./AvatarDisplay";
import { useTheme } from "@/components/context/ThemeContext";
import { useAuth } from "@/components/context/AuthContext";
import { updateAvatar } from "../../../services/api";
import { toast } from "react-toastify";

export default function AvatarSystem() {
  const { theme } = useTheme();
  const { fetchMe } = useAuth();
  const [activeTab, setActiveTab] = useState("upload");
  const [avatar, setAvatar] = useState(null);
  const [selectedTier, setSelectedTier] = useState("bronze");
  const [isSaving, setIsSaving] = useState(false);

  const handleAvatarUpload = (url) => {
    setAvatar({
      url,
      tier: selectedTier,
    });
  };

  const handleAvatarGenerate = (url, customization) => {
    setAvatar({
      url,
      tier: selectedTier,
      customization,
    });
  };

  const handleSaveAvatar = async () => {
    if (!avatar?.url) {
      toast.error("Please create or upload an avatar first");
      return;
    }

    try {
      setIsSaving(true);
      await updateAvatar(avatar.url);
      await fetchMe();
      toast.success("Avatar saved successfully!");
    } catch (err) {
      console.error("Error saving avatar:", err);
      toast.error(err.message || "Failed to save avatar");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`${styles.container} ${theme}`}>
      <div className={styles.header}>
        <h1 className={styles.title}>Avatar Creator</h1>
        <p className={styles.subtitle}>Create your unique pixel-style avatar</p>
      </div>

      <div className={styles.content}>
        {/* Tier Selection */}
        <div className={styles.tierSection}>
          <h2 className={styles.sectionTitle}>Select Your Tier</h2>
          <div className={styles.tierGrid}>
            {["bronze", "silver", "gold", "vip"].map((tier) => (
              <button
                key={tier}
                className={`${styles.tierButton} ${selectedTier === tier ? styles.tierActive : ""}`}
                onClick={() => setSelectedTier(tier)}
              >
                <span className={styles.tierLabel}>{tier.toUpperCase()}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className={styles.tabNav}>
          <button
            className={`${styles.tabButton} ${activeTab === "upload" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("upload")}
          >
            Upload Avatar
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === "generate" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("generate")}
          >
            Generate Avatar
          </button>
        </div>

        {/* Content Area */}
        <div className={styles.mainContent}>
          <div className={styles.editorSection}>
            {activeTab === "upload" ? (
              <AvatarUpload onUpload={handleAvatarUpload} />
            ) : (
              <AvatarGenerator onGenerate={handleAvatarGenerate} />
            )}
          </div>

          {/* Preview Section */}
          <div className={styles.previewSection}>
            <h2 className={styles.sectionTitle}>Preview</h2>
            {avatar ? (
              <AvatarDisplay avatar={avatar} tier={selectedTier} />
            ) : (
              <div className={styles.emptyPreview}>
                <p>Your avatar will appear here</p>
              </div>
            )}
          </div>
        </div>

        <div className="w-full flex justify-center items-center">
          <button
            className={styles.saveBtn}
            onClick={handleSaveAvatar}
            disabled={isSaving || !avatar}
          >
            {isSaving ? "Saving..." : "Save Avatar"}
          </button>
        </div>
      </div>
    </div>
  );
}
